import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, relTime, scopeQuery, useScope } from "../api";
import { friendlyAdminError } from "../apiError";
import { useT } from "../i18n";
import { useMe } from "./AdminShell";
import { Field, Msg, TextArea, TextInput, Toggle, useFlash, type Flash } from "../ui";

/** Fams board (admin spec §8): liveness, requests, matching, manual Fams and the Planning Center connection. */

interface FamRow {
  id: string;
  name: string;
  description: string | null;
  origin: "pco" | "manual" | "global";
  pcoGroupId: string | null;
  capacity: number | null;
  headerImage: string | null;
  churchCenterUrl: string | null;
  leaderUserId: string | null;
  leaderName: string | null;
  meetingDay: string | null;
  meetingTime: string | null;
  area: string | null;
  isBaptismFam: boolean;
  archived: boolean;
  members: number;
  appMembers: number;
  pcoMemberCount: number | null;
  requests: number;
  messages7d: number;
  activeMembers7d: number;
  lastMessageAt: string | null;
  liveness: "green" | "yellow" | "red" | "grey" | "archived";
}

interface Board {
  fams: FamRow[];
  requests: Array<{ famId: string; famName: string; userId: string; name: string; alias: string; requestedAt: string }>;
  matching: Array<{ id: string; userId: string; name: string; preferences: Record<string, string>; createdAt: string }>;
  planningCenter: { connected: boolean; syncedAt: string | null; syncError: string | null; churchCenterUrl: string | null } | null;
  members: { total: number; inFam: number; requested: number };
}

type FamInput = {
  name: string;
  description: string;
  capacity: string;
  headerImage: string;
  churchCenterUrl: string;
  leaderUserId: string;
  meetingDay: string;
  meetingTime: string;
  area: string;
  isBaptismFam: boolean;
  archived: boolean;
};

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;

const empty: FamInput = { name: "", description: "", capacity: "12", headerImage: "", churchCenterUrl: "", leaderUserId: "", meetingDay: "", meetingTime: "19:00", area: "", isBaptismFam: false, archived: false };

function toInput(f: FamRow): FamInput {
  return {
    name: f.name,
    description: f.description ?? "",
    capacity: f.capacity?.toString() ?? "",
    headerImage: f.headerImage ?? "",
    churchCenterUrl: f.churchCenterUrl ?? "",
    leaderUserId: f.leaderUserId ?? "",
    meetingDay: f.meetingDay ?? "",
    meetingTime: f.meetingTime ?? "",
    area: f.area ?? "",
    isBaptismFam: f.isBaptismFam,
    archived: f.archived,
  };
}

function toBody(i: FamInput) {
  return {
    name: i.name.trim(),
    description: i.description.trim() || null,
    capacity: i.capacity.trim() ? Number(i.capacity) : null,
    headerImage: i.headerImage.trim() || null,
    churchCenterUrl: i.churchCenterUrl.trim() || null,
    leaderUserId: i.leaderUserId.trim() || null,
    meetingDay: (i.meetingDay || null) as (typeof DAYS)[number] | null,
    meetingTime: i.meetingTime.trim() || null,
    area: i.area.trim() || null,
    isBaptismFam: i.isBaptismFam,
    archived: i.archived,
  };
}

export function AdminFams() {
  const { t, locale } = useT();
  const me = useMe();
  const scope = useScope();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin", "fams", scope.churchId], queryFn: () => api<Board>(`/admin/fams${scopeQuery(scope, "church")}`), refetchInterval: 60_000 });
  const invalidate = () => Promise.all([qc.invalidateQueries({ queryKey: ["admin", "fams"] }), qc.invalidateQueries({ queryKey: ["admin", "attention"] })]);
  const [flash, run] = useFlash();
  const [editing, setEditing] = useState<FamRow | "new" | null>(null);
  const [placing, setPlacing] = useState<{ id: string; famId: string } | null>(null);

  const sq = scopeQuery(scope, "church");
  const confirm = useMutation({ mutationFn: (p: { famId: string; userId: string; action: "confirm" | "decline" }) => api(`/admin/fams/${p.famId}/requests/${p.userId}/${p.action}${sq}`, { method: "POST" }), onSuccess: invalidate });
  const place = useMutation({ mutationFn: (p: { id: string; famId: string }) => api(`/admin/fams/matching/${p.id}/match${sq}`, { method: "POST", json: { famId: p.famId } }), onSuccess: () => void invalidate().then(() => setPlacing(null)) });
  const dismiss = useMutation({ mutationFn: (id: string) => api(`/admin/fams/matching/${id}${sq}`, { method: "DELETE" }), onSuccess: invalidate });

  const d = q.data;
  const noChurch = scope.canPickScope && !scope.churchId;
  const isAdmin = me.data?.isSuperadmin || me.data?.membership?.role === "admin";

  if (noChurch) {
    return (
      <>
        <h1>{t("admin.fams.title")}</h1>
        <p className="muted">{t("admin.fams.noChurch")}</p>
      </>
    );
  }

  return (
    <>
      <h1>{t("admin.fams.title")}</h1>
      <p className="muted">{t("admin.fams.body")}</p>
      {d ? <p className="muted small">{t("admin.fams.members", d.members)}</p> : null}
      {q.isError ? <p className="error">{t("admin.common.error")}</p> : null}
      <Msg msg={flash} />

      <div className="tiles two">
        <div className={`tile ${d && d.requests.length > 0 ? "warn" : ""}`}>
          <div className="l">{t("admin.fams.requests")}</div>
          <div className="n">{d?.requests.length ?? "–"}</div>
          {d?.requests.length === 0 ? <div className="muted">{t("admin.fams.requestsEmpty")}</div> : null}
          <div className="rows">
            {d?.requests.map((r) => (
              <div key={`${r.famId}:${r.userId}`} className="row fam-req">
                <div className="grow">
                  <strong>{r.name}</strong> <span className="muted">→ {r.famName}</span>
                  <div className="muted small">{relTime(r.requestedAt, locale)}</div>
                </div>
                <button className="btn small" disabled={confirm.isPending} onClick={() => confirm.mutate({ famId: r.famId, userId: r.userId, action: "confirm" })}>
                  {t("admin.fams.confirm")}
                </button>
                <button className="btn small ghost" disabled={confirm.isPending} onClick={() => confirm.mutate({ famId: r.famId, userId: r.userId, action: "decline" })}>
                  {t("admin.fams.decline")}
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="tile">
          <div className="l">{t("admin.fams.matching")}</div>
          <div className="n">{d?.matching.length ?? "–"}</div>
          {d?.matching.length === 0 ? <div className="muted">{t("admin.fams.matchingEmpty")}</div> : null}
          <div className="rows">
            {d?.matching.map((m) => (
              <div key={m.id} className="row fam-req">
                <div className="grow">
                  <strong>{m.name}</strong>
                  <div className="muted small">
                    {t("admin.fams.prefs")}:{" "}
                    {Object.entries(m.preferences)
                      .filter(([, v]) => v)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(" · ") || "–"}{" "}
                    · {relTime(m.createdAt, locale)}
                  </div>
                </div>
                {placing?.id === m.id ? (
                  <>
                    <select value={placing.famId} onChange={(e) => setPlacing({ id: m.id, famId: e.target.value })}>
                      <option value="">–</option>
                      {d?.fams
                        .filter((f) => !f.archived)
                        .map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.name}
                          </option>
                        ))}
                    </select>
                    <button className="btn small" disabled={!placing.famId || place.isPending} onClick={() => place.mutate(placing)}>
                      {t("admin.fams.place")}
                    </button>
                  </>
                ) : (
                  <button className="btn small" onClick={() => setPlacing({ id: m.id, famId: "" })}>
                    {t("admin.fams.place")}
                  </button>
                )}
                <button className="btn small ghost" onClick={() => dismiss.mutate(m.id)}>
                  {t("admin.fams.dismiss")}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <PlanningCenter pc={d?.planningCenter ?? null} isAdmin={Boolean(isAdmin)} run={run} invalidate={invalidate} />

      <div className="toolbar">
        <h2 className="section">{t("admin.fams.list")}</h2>
        <button className="btn small" onClick={() => setEditing("new")}>
          {t("admin.fams.new")}
        </button>
      </div>
      {editing ? (
        <FamForm
          initial={editing === "new" ? empty : toInput(editing)}
          origin={editing === "new" ? "manual" : editing.origin}
          onCancel={() => setEditing(null)}
          onSave={async (input) => {
            const ok = await run(async () => {
              if (editing === "new") await api(`/admin/fams${sq}`, { method: "POST", json: toBody(input) });
              else await api(`/admin/fams/${editing.id}${sq}`, { method: "PATCH", json: toBody(input) });
              await invalidate();
            }, t("admin.common.saved"));
            if (ok) setEditing(null);
          }}
        />
      ) : null}
      <div className="fam-table">
        <div className="fam-head">
          <span />
          <span>{t("admin.fams.name")}</span>
          <span>{t("admin.fams.colLeader")}</span>
          <span className="r">{t("admin.fams.colMembers")}</span>
          <span className="r">{t("admin.fams.colRequests")}</span>
          <span className="r">{t("admin.fams.colChat")}</span>
          <span>{t("admin.fams.colLast")}</span>
          <span />
        </div>
        {d?.fams.map((f) => (
          <div key={f.id} className={`fam-row ${f.archived ? "archived" : ""}`}>
            <span className={`dot ${f.liveness}`} title={t(`admin.fams.liveness.${f.liveness}`)} />
            <span className="grow">
              <strong>{f.name}</strong>
              <div className="muted small">
                {t(`admin.fams.origin.${f.origin}`)}
                {f.meetingDay ? ` · ${t(`community.day.${f.meetingDay}`)}${f.meetingTime ? ` ${f.meetingTime}` : ""}` : ""}
                {f.area ? ` · ${f.area}` : ""}
                {f.isBaptismFam ? ` · ${t("admin.fams.baptism")}` : ""}
                {f.archived ? ` · ${t("admin.fams.archived")}` : ""}
              </div>
            </span>
            <span className="muted">{f.leaderName ?? "–"}</span>
            <span className="r">
              {f.members}
              {f.capacity ? <span className="muted"> / {f.capacity}</span> : null}
            </span>
            <span className="r">{f.requests || "–"}</span>
            <span className="r">{f.messages7d || "–"}</span>
            <span className="muted small">{f.lastMessageAt ? relTime(f.lastMessageAt, locale) : t("admin.fams.never")}</span>
            <span>
              <button className="btn small ghost" onClick={() => setEditing(f)}>
                {t("admin.common.edit")}
              </button>
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

function PlanningCenter({ pc, isAdmin, run, invalidate }: { pc: Board["planningCenter"]; isAdmin: boolean; run: (fn: () => Promise<unknown>, ok?: string) => Promise<boolean>; invalidate: () => Promise<unknown> }) {
  const { t, locale } = useT();
  const scope = useScope();
  const sq = scopeQuery(scope, "church");
  const [appId, setAppId] = useState("");
  const [secret, setSecret] = useState("");
  const [syncMsg, setSyncMsg] = useState<Flash>(null);
  const sync = useMutation({
    mutationFn: () => api<{ sync: { groups: number; created: number; confirmed: number; error: string | null } }>(`/admin/fams/planning-center/sync${sq}`, { method: "POST" }),
    onSuccess: (r) => {
      setSyncMsg(r.sync.error ? { kind: "error", text: t("admin.fams.pcoError", { error: r.sync.error }) } : { kind: "success", text: t("admin.fams.pcoSynced", r.sync) });
      void invalidate();
    },
    onError: (e) => setSyncMsg({ kind: "error", text: friendlyAdminError(t, e) }),
  });
  if (!pc) return null;
  return (
    <div className="panel">
      <h2 className="section">{t("admin.fams.pcoTitle")}</h2>
      {pc.connected ? (
        <>
          <p className={pc.syncError ? "error" : "muted"}>{pc.syncError ? t("admin.fams.pcoError", { error: pc.syncError }) : pc.syncedAt ? t("admin.fams.pcoConnected", { when: relTime(pc.syncedAt, locale) }) : t("admin.fams.pcoNever")}</p>
          <Msg msg={syncMsg} />
          <div className="chips">
            <button className="btn small" disabled={sync.isPending} onClick={() => sync.mutate()}>
              {t("admin.fams.pcoSync")}
            </button>
            {isAdmin ? (
              <button className="btn small danger ghost" onClick={() => void run(() => api(`/admin/fams/planning-center${sq}`, { method: "DELETE" }).then(invalidate))}>
                {t("admin.fams.pcoDisconnect")}
              </button>
            ) : null}
          </div>
        </>
      ) : (
        <>
          <p className="muted">{t("admin.fams.pcoNot")}</p>
          {isAdmin ? (
            <form
              className="form inner"
              onSubmit={(e) => {
                e.preventDefault();
                void run(() => api(`/admin/fams/planning-center${sq}`, { method: "PUT", json: { appId, secret } }).then(invalidate), t("admin.common.saved"));
              }}
            >
              <div className="two-col">
                <Field label={t("admin.fams.pcoAppId")}>
                  <TextInput value={appId} onChange={(e) => setAppId(e.target.value)} required />
                </Field>
                <Field label={t("admin.fams.pcoSecret")}>
                  <TextInput type="password" value={secret} onChange={(e) => setSecret(e.target.value)} required />
                </Field>
              </div>
              <p className="muted small">{t("admin.fams.pcoHint")}</p>
              <button className="btn small" type="submit">
                {t("admin.fams.pcoConnect")}
              </button>
            </form>
          ) : null}
        </>
      )}
    </div>
  );
}

function FamForm({ initial, origin, onSave, onCancel }: { initial: FamInput; origin: FamRow["origin"]; onSave: (i: FamInput) => Promise<void>; onCancel: () => void }) {
  const { t } = useT();
  const [v, setV] = useState(initial);
  const set = <K extends keyof FamInput>(k: K, val: FamInput[K]) => setV((s) => ({ ...s, [k]: val }));
  const pco = origin === "pco";
  return (
    <form
      className="form"
      onSubmit={(e) => {
        e.preventDefault();
        void onSave(v);
      }}
    >
      <h3>{initial.name ? t("admin.fams.edit") : t("admin.fams.new")}</h3>
      {pco ? <p className="muted small">{t("admin.fams.pcoReadonly")}</p> : null}
      <div className="two-col">
        <Field label={t("admin.fams.name")}>
          <TextInput value={v.name} onChange={(e) => set("name", e.target.value)} required disabled={pco} />
        </Field>
        <Field label={t("admin.fams.area")}>
          <TextInput value={v.area} onChange={(e) => set("area", e.target.value)} />
        </Field>
      </div>
      <Field label={t("admin.fams.description")}>
        <TextArea value={v.description} onChange={(e) => set("description", e.target.value)} rows={2} disabled={pco} />
      </Field>
      <div className="two-col">
        <Field label={t("admin.fams.meetingDay")}>
          <select value={v.meetingDay} onChange={(e) => set("meetingDay", e.target.value)} disabled={pco}>
            <option value="">–</option>
            {DAYS.map((d) => (
              <option key={d} value={d}>
                {t(`community.day.${d}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("admin.fams.meetingTime")}>
          <TextInput type="time" value={v.meetingTime} onChange={(e) => set("meetingTime", e.target.value)} disabled={pco} />
        </Field>
      </div>
      <div className="two-col">
        <Field label={t("admin.fams.capacity")}>
          <TextInput type="number" min={1} max={500} value={v.capacity} onChange={(e) => set("capacity", e.target.value)} />
        </Field>
        <Field label={t("admin.fams.leader")} hint={t("admin.fams.leaderHint")}>
          <TextInput value={v.leaderUserId} onChange={(e) => set("leaderUserId", e.target.value)} />
        </Field>
      </div>
      {!pco ? (
        <div className="two-col">
          <Field label={t("admin.fams.churchCenterUrl")}>
            <TextInput type="url" value={v.churchCenterUrl} onChange={(e) => set("churchCenterUrl", e.target.value)} />
          </Field>
          <Field label={t("admin.fams.headerImage")}>
            <TextInput type="url" value={v.headerImage} onChange={(e) => set("headerImage", e.target.value)} />
          </Field>
        </div>
      ) : null}
      <div className="chips">
        <Toggle label={t("admin.fams.baptism")} checked={v.isBaptismFam} onChange={(c) => set("isBaptismFam", c)} />
        {!pco ? <Toggle label={t("admin.fams.archived")} checked={v.archived} onChange={(c) => set("archived", c)} /> : null}
      </div>
      <div className="chips">
        <button className="btn small" type="submit">
          {t("admin.common.save")}
        </button>
        <button className="btn small ghost" type="button" onClick={onCancel}>
          {t("admin.common.cancel")}
        </button>
      </div>
    </form>
  );
}
